// Financial Data Error Handler - Comprehensive error handling and fallback mechanisms
// Provides robust error handling for JSON scraper integration
// Last updated: January 2025

import { FinancialConfig } from './FinancialConfig.js';

export class FinancialDataErrorHandler {
    static _errorLog = [];
    static _maxErrorLogSize = 100;
    static _fallbackMode = false;

    /**
     * Handle errors during financial data loading
     * @param {Error} error - The error that occurred
     * @param {string} context - Context where the error occurred
     * @param {Object} fallbackData - Fallback data to use
     * @returns {Object} Fallback data or error response
     */
    static handleError(error, context, fallbackData = null) {
        const errorInfo = {
            timestamp: new Date().toISOString(),
            context: context,
            error: {
                message: error.message,
                stack: error.stack,
                name: error.name
            },
            fallbackUsed: !!fallbackData
        };

        // Log the error
        this._logError(errorInfo);

        // Set fallback mode if this is a critical error
        if (this._isCriticalError(error)) {
            this._fallbackMode = true;
            console.warn('Critical error detected, switching to fallback mode');
        }

        // Return fallback data or error response
        if (fallbackData) {
            console.warn(`Using fallback data for ${context}:`, error.message);
            return {
                data: fallbackData,
                source: 'fallback',
                error: errorInfo,
                warning: `Data loaded from fallback due to error: ${error.message}`
            };
        }

        return {
            data: null,
            source: 'error',
            error: errorInfo,
            message: `Failed to load data for ${context}: ${error.message}`
        };
    }

    /**
     * Check if an error is critical enough to trigger fallback mode
     * @param {Error} error - The error to check
     * @returns {boolean} True if critical
     */
    static _isCriticalError(error) {
        const criticalErrors = [
            'NetworkError',
            'TimeoutError',
            'DataCorruptionError',
            'ValidationError'
        ];

        return criticalErrors.some(criticalError => 
            error.name === criticalError || 
            error.message.includes(criticalError)
        );
    }

    /**
     * Log error information
     * @param {Object} errorInfo - Error information to log
     */
    static _logError(errorInfo) {
        this._errorLog.push(errorInfo);
        
        // Keep only the most recent errors
        if (this._errorLog.length > this._maxErrorLogSize) {
            this._errorLog = this._errorLog.slice(-this._maxErrorLogSize);
        }

        // Log to console in development
        if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
            console.error('Financial Data Error:', errorInfo);
        }
    }

    /**
     * Get error log
     * @returns {Array} Array of error information
     */
    static getErrorLog() {
        return [...this._errorLog];
    }

    /**
     * Clear error log
     */
    static clearErrorLog() {
        this._errorLog = [];
    }

    /**
     * Check if system is in fallback mode
     * @returns {boolean} True if in fallback mode
     */
    static isInFallbackMode() {
        return this._fallbackMode;
    }

    /**
     * Reset fallback mode
     */
    static resetFallbackMode() {
        this._fallbackMode = false;
    }

    /**
     * Validate financial data structure
     * @param {Object} data - Data to validate
     * @param {string} dataType - Type of data being validated
     * @returns {Object} Validation result
     */
    static validateFinancialData(data, dataType) {
        const validationResult = {
            isValid: true,
            errors: [],
            warnings: [],
            data: data
        };

        if (!data || typeof data !== 'object') {
            validationResult.isValid = false;
            validationResult.errors.push('Data is not a valid object');
            return validationResult;
        }

        // Validate based on data type
        switch (dataType) {
            case 'contributionCaps':
                this._validateContributionCaps(data, validationResult);
                break;
            case 'smsfFees':
                this._validateSMSFFees(data, validationResult);
                break;
            case 'minimumPensionStandards':
                this._validateMinimumPensionStandards(data, validationResult);
                break;
            case 'agePension':
                this._validateAgePension(data, validationResult);
                break;
            default:
                validationResult.warnings.push(`Unknown data type: ${dataType}`);
        }

        return validationResult;
    }

    /**
     * Validate contribution caps data
     * @param {Object} data - Contribution caps data
     * @param {Object} validationResult - Validation result object to update
     */
    static _validateContributionCaps(data, validationResult) {
        const requiredFields = ['concessional', 'nonConcessional'];
        
        requiredFields.forEach(field => {
            if (!data.hasOwnProperty(field)) {
                validationResult.errors.push(`Missing required field: ${field}`);
                validationResult.isValid = false;
            } else if (typeof data[field] !== 'number' || data[field] <= 0) {
                validationResult.errors.push(`Invalid ${field}: must be a positive number`);
                validationResult.isValid = false;
            }
        });

        // Check for reasonable values
        if (data.concessional && (data.concessional < 10000 || data.concessional > 100000)) {
            validationResult.warnings.push(`Concessional cap seems unusual: $${data.concessional}`);
        }

        if (data.nonConcessional && (data.nonConcessional < 50000 || data.nonConcessional > 500000)) {
            validationResult.warnings.push(`Non-concessional cap seems unusual: $${data.nonConcessional}`);
        }
    }

    /**
     * Validate SMSF fees data
     * @param {Object} data - SMSF fees data
     * @param {Object} validationResult - Validation result object to update
     */
    static _validateSMSFFees(data, validationResult) {
        const requiredSections = ['trusteeFees', 'auditCosts', 'complianceCosts'];
        
        requiredSections.forEach(section => {
            if (!data.hasOwnProperty(section)) {
                validationResult.errors.push(`Missing required SMSF fees section: ${section}`);
                validationResult.isValid = false;
            }
        });

        // Validate trustee fees structure
        if (data.trusteeFees) {
            if (!data.trusteeFees.individual || !data.trusteeFees.corporate) {
                validationResult.errors.push('Trustee fees must include both individual and corporate structures');
                validationResult.isValid = false;
            }
        }
    }

    /**
     * Validate minimum pension standards data
     * @param {Object} data - Minimum pension standards data
     * @param {Object} validationResult - Validation result object to update
     */
    static _validateMinimumPensionStandards(data, validationResult) {
        if (!data.withdrawalRates || typeof data.withdrawalRates !== 'object') {
            validationResult.errors.push('Missing or invalid withdrawalRates');
            validationResult.isValid = false;
            return;
        }

        // Check for required age ranges
        const requiredAges = [65, 70, 75, 80, 85, 90, 95];
        requiredAges.forEach(age => {
            if (!data.withdrawalRates.hasOwnProperty(age)) {
                validationResult.warnings.push(`Missing withdrawal rate for age ${age}`);
            } else {
                const rate = data.withdrawalRates[age];
                if (typeof rate !== 'number' || rate <= 0 || rate > 0.2) {
                    validationResult.errors.push(`Invalid withdrawal rate for age ${age}: ${rate}`);
                    validationResult.isValid = false;
                }
            }
        });
    }

    /**
     * Validate age pension data
     * @param {Object} data - Age pension data
     * @param {Object} validationResult - Validation result object to update
     */
    static _validateAgePension(data, validationResult) {
        if (!data.rates || typeof data.rates !== 'object') {
            validationResult.errors.push('Missing or invalid age pension rates');
            validationResult.isValid = false;
        }

        if (!data.assetThresholds || typeof data.assetThresholds !== 'object') {
            validationResult.errors.push('Missing or invalid asset thresholds');
            validationResult.isValid = false;
        }
    }

    /**
     * Create fallback data for a specific data type
     * @param {string} dataType - Type of data to create fallback for
     * @returns {Object} Fallback data
     */
    static createFallbackData(dataType) {
        switch (dataType) {
            case 'contributionCaps':
                return FinancialConfig.CONTRIBUTION_CAPS;
            case 'smsfFees':
                return FinancialConfig.SMSF.FEES;
            case 'minimumPensionStandards':
                return FinancialConfig.MINIMUM_PENSION_STANDARDS;
            case 'agePension':
                return FinancialConfig.AGE_PENSION;
            case 'transferBalanceCap':
                return { TRANSFER_BALANCE_CAP: FinancialConfig.TRANSFER_BALANCE_CAP };
            default:
                return {};
        }
    }

    /**
     * Merge fallback data with loaded data
     * @param {Object} loadedData - Data loaded from external source
     * @param {Object} fallbackData - Fallback data to merge
     * @returns {Object} Merged data
     */
    static mergeWithFallback(loadedData, fallbackData) {
        if (!loadedData || typeof loadedData !== 'object') {
            return fallbackData;
        }

        if (!fallbackData || typeof fallbackData !== 'object') {
            return loadedData;
        }

        // Deep merge the objects
        const merged = { ...fallbackData };
        
        Object.keys(loadedData).forEach(key => {
            if (loadedData[key] !== null && loadedData[key] !== undefined) {
                if (typeof loadedData[key] === 'object' && !Array.isArray(loadedData[key])) {
                    merged[key] = this.mergeWithFallback(loadedData[key], fallbackData[key] || {});
                } else {
                    merged[key] = loadedData[key];
                }
            }
        });

        return merged;
    }

    /**
     * Get system health status
     * @returns {Object} System health information
     */
    static getSystemHealth() {
        const recentErrors = this._errorLog.filter(error => {
            const errorTime = new Date(error.timestamp);
            const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
            return errorTime > oneHourAgo;
        });

        const criticalErrors = recentErrors.filter(error => 
            this._isCriticalError({ name: error.error.name, message: error.error.message })
        );

        return {
            isHealthy: criticalErrors.length === 0,
            fallbackMode: this._fallbackMode,
            recentErrorCount: recentErrors.length,
            criticalErrorCount: criticalErrors.length,
            totalErrorCount: this._errorLog.length,
            lastError: this._errorLog.length > 0 ? this._errorLog[this._errorLog.length - 1] : null,
            recommendations: this._getHealthRecommendations(recentErrors, criticalErrors)
        };
    }

    /**
     * Get health recommendations based on error patterns
     * @param {Array} recentErrors - Recent errors
     * @param {Array} criticalErrors - Critical errors
     * @returns {Array} Array of recommendations
     */
    static _getHealthRecommendations(recentErrors, criticalErrors) {
        const recommendations = [];

        if (criticalErrors.length > 0) {
            recommendations.push('System is experiencing critical errors. Consider checking network connectivity and data sources.');
        }

        if (recentErrors.length > 10) {
            recommendations.push('High error rate detected. Consider implementing retry mechanisms or alternative data sources.');
        }

        if (this._fallbackMode) {
            recommendations.push('System is in fallback mode. Verify external data sources are functioning correctly.');
        }

        if (recentErrors.length === 0) {
            recommendations.push('System is operating normally with no recent errors.');
        }

        return recommendations;
    }

    /**
     * Retry mechanism for failed operations
     * @param {Function} operation - Operation to retry
     * @param {number} maxRetries - Maximum number of retries
     * @param {number} delay - Delay between retries in milliseconds
     * @returns {Promise} Promise that resolves with operation result
     */
    static async retryOperation(operation, maxRetries = 3, delay = 1000) {
        let lastError;
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                return await operation();
            } catch (error) {
                lastError = error;
                
                if (attempt === maxRetries) {
                    throw error;
                }
                
                // Exponential backoff
                const waitTime = delay * Math.pow(2, attempt - 1);
                await new Promise(resolve => setTimeout(resolve, waitTime));
                
                console.warn(`Retry attempt ${attempt} failed, retrying in ${waitTime}ms...`);
            }
        }
        
        throw lastError;
    }
}

// Export default instance
export default FinancialDataErrorHandler;


